/**
 * royalty-statements.tsx
 *
 * Royalty Statement Management Page - Person-First Flow
 * 1. Search for author by name, account number, or email
 * 2. Select author to see their available periods
 * 3. Preview, download PDF, or send email for each period
 */

import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Loader2,
  FileText,
  Mail,
  Download,
  Eye,
  Search,
  ArrowLeft,
  User,
  Calendar,
  DollarSign,
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Receipt,
  Info,
  Clock,
  AlertTriangle,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useSlowLoadingFlag } from "@/hooks/useSlowLoadingFlag";
import { PageWrapper } from "@/components/layout/PageWrapper";
import { ViewGuard } from "@/components/auth/PermissionGuards";
import { SlowLoadingNotice } from "@/components/shared/SlowLoadingNotice";
import {
  searchAuthors,
  getAccountSummary,
  getAuthorEmail,
  getSentStatus,
  getQuarterlyStatements,
  getQuarterlyLines,
  getQuarterlyStatementData,
  downloadQuarterlyPdf,
  sendQuarterlyEmail,
  downloadBulkQuarterlyPdf,
  sendBulkQuarterlyEmail,
  getPaymentHistory,
  getCcRecipients,
  AuthorSearchResult,
  AuthorEmailEntry,
  RoyaltyStatementData,
  RoyaltyStatementLine,
  RoyaltySettlementHeader,
  QuarterlyStatement,
  PaymentHistoryRow,
  DuplicateSendError,
} from "@/lib/royalty-api";
import { isValidEmail } from "@/lib/validation";
import { Checkbox } from "@/components/ui/checkbox";
import debounce from "lodash/debounce";

// ============================================================================
// Helper Functions
// ============================================================================

const formatPeriod = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
};

const formatCurrency = (value: number | null | undefined, currency: string = "EUR"): string => {
  if (value === null || value === undefined) return "-";
  const symbol =
    currency === "USD" ? "$" :
    currency === "GBP" ? "£" :
    currency === "CHF" ? "CHF " : "€";
  return `${symbol}${value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatNumber = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return "-";
  return value.toLocaleString("en-US");
};

const formatDecimal = (value: number | null | undefined, decimals: number = 2): string => {
  if (value === null || value === undefined) return "-";
  return value.toFixed(decimals);
};

// ============================================================================
// Grouped Data Types for Hierarchical Display
// ============================================================================

interface GroupedByPeriod {
  settlementPeriod: string;
  lines: RoyaltyStatementLine[];
  subtotals: {
    copies: number;
    returns: number;
    ungross: number;
    royalty: number;
  };
}

interface GroupedStatementData {
  accountNumber: number;
  authorName: string;
  periods: GroupedByPeriod[];
}

/**
 * Group line items by account and settlement period for hierarchical display
 */
const groupStatementData = (lines: RoyaltyStatementLine[]): GroupedStatementData[] => {
  // First group by account number
  const byAccount = new Map<number, { authorName: string; lines: RoyaltyStatementLine[] }>();

  for (const line of lines) {
    if (!byAccount.has(line.accountNumber)) {
      byAccount.set(line.accountNumber, { authorName: line.authorName, lines: [] });
    }
    byAccount.get(line.accountNumber)!.lines.push(line);
  }

  // Then group each account's lines by period
  const result: GroupedStatementData[] = [];

  for (const [accountNumber, { authorName, lines: accountLines }] of Array.from(byAccount)) {
    const byPeriod = new Map<string, RoyaltyStatementLine[]>();

    for (const line of accountLines) {
      const periodKey = line.settlementPeriod;
      if (!byPeriod.has(periodKey)) {
        byPeriod.set(periodKey, []);
      }
      byPeriod.get(periodKey)!.push(line);
    }

    const periods: GroupedByPeriod[] = [];
    for (const [period, periodLines] of Array.from(byPeriod)) {
      periods.push({
        settlementPeriod: period,
        lines: periodLines,
        subtotals: {
          copies: periodLines.reduce((sum: number, l: RoyaltyStatementLine) => sum + l.copies, 0),
          returns: periodLines.reduce((sum: number, l: RoyaltyStatementLine) => sum + l.returns, 0),
          ungross: periodLines.reduce((sum: number, l: RoyaltyStatementLine) => sum + (l.ungross ?? (l.copies + l.returns)), 0),
          royalty: periodLines.reduce((sum: number, l: RoyaltyStatementLine) => sum + l.royalty, 0),
        }
      });
    }

    // Sort periods by date descending
    periods.sort((a, b) => new Date(b.settlementPeriod).getTime() - new Date(a.settlementPeriod).getTime());

    result.push({ accountNumber, authorName, periods });
  }

  return result;
};

// ============================================================================
// Author Card Component
// ============================================================================

const AuthorCard = ({
  author,
  onClick,
}: {
  author: AuthorSearchResult;
  onClick: () => void;
}) => {
  return (
    <Card
      className="cursor-pointer hover:bg-muted/50 transition-colors"
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{author.authorName}</span>
            </div>
            <div className="mt-1 text-sm text-muted-foreground">
              Account: {author.accountNumber}
            </div>
            {author.email && (
              <div className="mt-1 text-sm text-blue-600">
                {author.email}
              </div>
            )}
          </div>
          <div className="text-right">
            <Badge variant="secondary">
              {author.periodCount} {author.periodCount === 1 ? "period" : "periods"}
            </Badge>
            <div className="mt-1 text-sm text-muted-foreground">
              {formatCurrency(author.totalRoyaltiesAllTime)}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Group search results so an author's multiple accounts collapse into one
// hierarchical card (Sanjay feedback). Name is the only key that unifies an
// author's accounts (their accounts carry different creditor + juristic-person
// ids); results whose name fell back to the account number stay solo.
type SearchAuthorGroup = {
  key: string;
  authorName: string;
  authors: AuthorSearchResult[];
  totalRoyalties: number;
  totalPeriods: number;
};

const groupAuthorSearchResults = (authors: AuthorSearchResult[]): SearchAuthorGroup[] => {
  const map = new Map<string, AuthorSearchResult[]>();
  for (const a of authors) {
    const name = (a.authorName || "").trim();
    const fl = `${(a.authorFirstName || "").trim()} ${(a.authorLastName || "").trim()}`.trim().toLowerCase();
    const isFallback = !name || /^\d+$/.test(name);
    const key = isFallback || !fl ? `acct:${a.creditorId}` : `name:${fl}`;
    let arr = map.get(key);
    if (!arr) { arr = []; map.set(key, arr); }
    arr.push(a);
  }
  const groups: SearchAuthorGroup[] = [];
  for (const [key, list] of map) {
    list.sort((x, y) => (y.totalRoyaltiesAllTime ?? 0) - (x.totalRoyaltiesAllTime ?? 0));
    groups.push({
      key,
      authorName: list[0].authorName,
      authors: list,
      totalRoyalties: list.reduce((s, a) => s + (a.totalRoyaltiesAllTime ?? 0), 0),
      totalPeriods: list.reduce((s, a) => s + (a.periodCount ?? 0), 0),
    });
  }
  return groups;
};

// A multi-account author: one parent card that expands to its accounts.
const GroupedAuthorCard = ({
  group,
  onSelect,
}: {
  group: SearchAuthorGroup;
  onSelect: (author: AuthorSearchResult) => void;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <Card>
      <CardContent className="p-4">
        <button
          type="button"
          className="flex w-full items-start justify-between text-left"
          onClick={() => setOpen((o) => !o)}
        >
          <div className="flex-1">
            <div className="flex items-center gap-2">
              {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{group.authorName}</span>
              <Badge variant="secondary">{group.authors.length} accounts</Badge>
            </div>
            <div className="mt-1 ml-6 text-sm text-muted-foreground">
              Accounts: {group.authors.map((a) => a.accountNumber).join(", ")}
            </div>
          </div>
          <div className="text-right">
            <Badge variant="secondary">
              {group.totalPeriods} {group.totalPeriods === 1 ? "period" : "periods"}
            </Badge>
            <div className="mt-1 text-sm text-muted-foreground">
              {formatCurrency(group.totalRoyalties)}
            </div>
          </div>
        </button>
        {open && (
          <div className="mt-3 space-y-2 border-t pt-3">
            {group.authors.map((a) => (
              <button
                key={a.creditorId}
                type="button"
                onClick={() => onSelect(a)}
                className="flex w-full items-center justify-between rounded-md border px-3 py-2 text-left hover:bg-muted/50"
              >
                <div>
                  <div className="text-sm font-medium">Account: {a.accountNumber}</div>
                  {a.email && <div className="text-xs text-blue-600">{a.email}</div>}
                </div>
                <div className="text-right">
                  <Badge variant="outline">
                    {a.periodCount} {a.periodCount === 1 ? "period" : "periods"}
                  </Badge>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {formatCurrency(a.totalRoyaltiesAllTime)}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ============================================================================
// Preview Dialog - Enhanced with Hierarchical Display & Payment Summary
// ============================================================================

/**
 * Payment Summary Component - Shows settlement header info when SAP data available
 */
const PaymentSummaryCard = ({ header }: { header: RoyaltySettlementHeader | null }) => {
  if (!header) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">Payment Summary</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell className="font-medium">Last Account Balance</TableCell>
              <TableCell className="text-right">{formatCurrency(header.lastAccountBalance)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Gross Amount Paid</TableCell>
              <TableCell className="text-right">{formatCurrency(header.grossAmountPaid)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Sub Total</TableCell>
              <TableCell className="text-right">{formatCurrency(header.subTotal)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">VAT Amount</TableCell>
              <TableCell className="text-right">{formatCurrency(header.vatAmount)}</TableCell>
            </TableRow>
            <TableRow className="bg-muted/50">
              <TableCell className="font-bold">Net Payment</TableCell>
              <TableCell className="text-right font-bold text-primary text-lg">
                {formatCurrency(header.netPayment)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

/**
 * Line Item Detail Table - Shows individual title rows with all Power BI columns
 */
const LineItemTable = ({
  lines,
  currency,
}: {
  lines: RoyaltyStatementLine[];
  currency: string;
}) => {
  // Check if we have enhanced data (Power BI parity fields)
  const hasEnhancedData = lines.some(l => l.ungross !== null || l.rateOrigin !== null);

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="min-w-[200px]">Title / ISBN</TableHead>
            <TableHead className="text-right">Copies</TableHead>
            <TableHead className="text-right">Returns</TableHead>
            {hasEnhancedData && (
              <>
                <TableHead className="text-right">Ungross</TableHead>
                <TableHead className="text-right">Rate</TableHead>
                <TableHead className="text-right">Base</TableHead>
                <TableHead className="text-right">Revenue</TableHead>
              </>
            )}
            {!hasEnhancedData && (
              <TableHead className="text-right">Rate</TableHead>
            )}
            <TableHead className="text-right">Royalty</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {lines.map((line, i) => (
            <TableRow key={i}>
              <TableCell className="font-mono text-sm">
                {line.titleDetails || line.isbn}
              </TableCell>
              <TableCell className="text-right">{formatNumber(line.copies)}</TableCell>
              <TableCell className="text-right">{formatNumber(line.returns)}</TableCell>
              {hasEnhancedData && (
                <>
                  <TableCell className="text-right">
                    {formatNumber(line.ungross ?? (line.copies + line.returns))}
                  </TableCell>
                  <TableCell className="text-right">
                    {line.rateOrigin ? `${formatDecimal(line.rateOrigin, 1)}%` : (line.slidingRate ? `${formatDecimal(line.slidingRate, 1)}%` : "-")}
                  </TableCell>
                  <TableCell className="text-right">
                    {line.royaltyBase ? formatCurrency(line.royaltyBase, currency) : "-"}
                  </TableCell>
                  <TableCell className="text-right">
                    {line.totalRevenue ? formatCurrency(line.totalRevenue, currency) : (line.revenue ? formatCurrency(line.revenue, currency) : "-")}
                  </TableCell>
                </>
              )}
              {!hasEnhancedData && (
                <TableCell className="text-right">
                  {line.slidingRate ? `${formatDecimal(line.slidingRate, 1)}%` : "-"}
                </TableCell>
              )}
              <TableCell className="text-right font-medium">
                {formatCurrency(line.royalty, currency)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

/**
 * Enhanced Preview Dialog with hierarchical grouping and payment summary
 */
const PreviewDialog = ({
  open,
  onOpenChange,
  statementData,
  isLoading,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  statementData: RoyaltyStatementData | null;
  isLoading: boolean;
}) => {
  // Track expanded state for collapsible sections
  const [expandedPeriods, setExpandedPeriods] = useState<Set<string>>(new Set());

  // Group data for hierarchical display - must be before any early returns (React hooks rule)
  const groupedData = useMemo(() =>
    statementData?.lines ? groupStatementData(statementData.lines) : [],
    [statementData?.lines]
  );

  if (!statementData && !isLoading) return null;

  const summary = statementData?.summary;
  const header = statementData?.header;

  const togglePeriod = (key: string) => {
    setExpandedPeriods(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Expand all periods by default
  const expandAll = () => {
    const allKeys = new Set<string>();
    groupedData.forEach(account => {
      account.periods.forEach(period => {
        allKeys.add(`${account.accountNumber}-${period.settlementPeriod}`);
      });
    });
    setExpandedPeriods(allKeys);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Statement Preview</DialogTitle>
          <DialogDescription>
            {summary ? `${summary.authorName} - ${statementData?.periodLabel || formatPeriod(summary.settlementPeriod)}` : "Loading..."}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : summary ? (
          <div className="space-y-6">
            {/* Summary Card - Matches Power BI layout */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <div className="text-sm text-muted-foreground">Account</div>
                    <div className="font-medium">{summary.accountNumber}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Titles</div>
                    <div className="font-medium">{summary.titleCount}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Last Balance</div>
                    <div className="font-medium">
                      {header ? formatCurrency(header.lastAccountBalance, summary.currency) : '-'}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Total Royalties</div>
                    <div className="font-medium">
                      {formatCurrency(summary.totalRoyalties, summary.currency)}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Email Status */}
            {statementData.authorEmail && (
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                <span>Email found: {statementData.authorEmail}</span>
              </div>
            )}
            {!statementData.authorEmail && (
              <div className="flex items-center gap-2 text-sm text-amber-600">
                <AlertCircle className="h-4 w-4" />
                <span>No email address found</span>
              </div>
            )}

            {/* Detail by Title - Hierarchical Collapsible */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">
                    Detail by Title ({statementData.lines.length} items)
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={expandAll}>
                    Expand All
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {groupedData.map((accountGroup) => (
                    <div key={accountGroup.accountNumber} className="border rounded-lg">
                      {/* Account Header - always visible since we typically have one account */}
                      <div className="bg-muted/30 px-4 py-2 font-medium border-b">
                        Account: {accountGroup.accountNumber} - {accountGroup.authorName}
                      </div>

                      {/* Period Groups */}
                      {accountGroup.periods.map((periodGroup) => {
                        const periodKey = `${accountGroup.accountNumber}-${periodGroup.settlementPeriod}`;
                        const isExpanded = expandedPeriods.has(periodKey);

                        return (
                          <Collapsible
                            key={periodKey}
                            open={isExpanded}
                            onOpenChange={() => togglePeriod(periodKey)}
                          >
                            <CollapsibleTrigger className="flex w-full items-center justify-between px-4 py-2 hover:bg-muted/50 transition-colors">
                              <div className="flex items-center gap-2">
                                {isExpanded ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                )}
                                <span className="font-medium">
                                  {formatPeriod(periodGroup.settlementPeriod)}
                                </span>
                                <Badge variant="secondary" className="ml-2">
                                  {periodGroup.lines.length} items
                                </Badge>
                                {periodGroup.lines.some((l) => l.isPreSapMigration) && (
                                  <Badge
                                    variant="outline"
                                    className="ml-1 border-amber-300 bg-amber-50 text-amber-800"
                                    title="Pre-dates the 2016 SAP migration; not reflected in the SAP ageing report."
                                  >
                                    Pre-SAP
                                  </Badge>
                                )}
                              </div>
                              <div className="text-right text-sm text-muted-foreground">
                                {formatCurrency(periodGroup.subtotals.royalty, summary.currency)}
                              </div>
                            </CollapsibleTrigger>
                            <CollapsibleContent className="border-t">
                              <div className="p-2">
                                <LineItemTable
                                  lines={periodGroup.lines}
                                  currency={summary.currency}
                                />
                              </div>
                            </CollapsibleContent>
                          </Collapsible>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Payment Summary - Only shown when SAP data available */}
            <PaymentSummaryCard header={header ?? null} />
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// Email Dialog
// ============================================================================

const getDefaultSingleMessage = (authorName: string, _periodDisplay: string, bankAccountLast4?: string | null): string =>
  `Dear ${authorName},

I hope this message finds you well.

Please find attached your royalty statement(s) for your reference.

${bankAccountLast4
    ? `May I kindly request you to confirm your bank account details ending with ${bankAccountLast4}, so that we can proceed with the payment accordingly. If your bank details changed, we kindly request you to fill the attached wire transfer form and return it to us at your convenience.`
    : `We kindly request you to fill the attached wire transfer form and return it to us at your convenience.`}

This will help us process the payment smoothly and without any delay.

Should you have any questions regarding the statement or the payment process, please don't hesitate to contact us at royalties@peterlang.com.

Kind regards,
Your Peter Lang Royalty Team`;

const DEFAULT_SUBJECT_SINGLE = "Your Peter Lang Royalty Statement";
const DEFAULT_SUBJECT_BULK = "Your Peter Lang Royalty Statements";

const EmailDialog = ({
  open,
  onOpenChange,
  authorName,
  period,
  periodDisplay,
  authorEmail,
  authorEmails,
  totalRoyalties,
  currency,
  bankAccountLast4,
  onSend,
  isSending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  authorName: string;
  period: string;
  periodDisplay?: string;
  authorEmail: string | null;
  authorEmails?: AuthorEmailEntry[];
  totalRoyalties: number;
  currency: string;
  bankAccountLast4?: string | null;
  onSend: (email: string, customMessage?: string, customSubject?: string, cc?: string[]) => void;
  isSending: boolean;
}) => {
  const [email, setEmail] = useState(authorEmail || "");
  const [subject, setSubject] = useState(DEFAULT_SUBJECT_SINGLE);
  const [useCustomEmail, setUseCustomEmail] = useState(false);
  const [selectedCc, setSelectedCc] = useState<Set<string>>(new Set());
  const ccInitializedRef = useRef(false);
  const ccQuery = useQuery({
    queryKey: ["royalty-cc-recipients", "active"],
    queryFn: () => getCcRecipients(true),
    enabled: open,
  });
  const displayPeriod = periodDisplay || formatPeriod(period);
  const defaultMessage = useMemo(
    () => getDefaultSingleMessage(authorName, displayPeriod, bankAccountLast4),
    [authorName, displayPeriod, bankAccountLast4]
  );
  const [message, setMessage] = useState(defaultMessage);
  const [showMessage, setShowMessage] = useState(false);

  // Reset state when the dialog opens for a different quarter/period
  useEffect(() => {
    if (open) {
      setEmail(authorEmail || "");
      setSubject(DEFAULT_SUBJECT_SINGLE);
      setMessage(defaultMessage);
      setShowMessage(false);
      setUseCustomEmail(false);
      setSelectedCc(new Set());
    }
  }, [open, defaultMessage, authorEmail]);

  // Default-select all active CC recipients when the dialog opens (once per open session).
  // Users can untick any they don't want; keeps them from re-adding stakeholders every send.
  useEffect(() => {
    if (!open) {
      ccInitializedRef.current = false;
      return;
    }
    if (ccQuery.data && !ccInitializedRef.current) {
      setSelectedCc(new Set(ccQuery.data.map((r) => r.emailAddress)));
      ccInitializedRef.current = true;
    }
  }, [open, ccQuery.data]);

  const isCustomMessage = message.trim() !== defaultMessage.trim();
  const isCustomSubject = subject.trim() !== DEFAULT_SUBJECT_SINGLE;
  const hasMultipleEmails = authorEmails && authorEmails.length > 1;
  const emailValid = isValidEmail(email);

  const handleSend = () => {
    if (emailValid) {
      onSend(
        email,
        isCustomMessage ? message : undefined,
        isCustomSubject ? subject : undefined,
        selectedCc.size > 0 ? Array.from(selectedCc) : undefined
      );
    }
  };

  const toggleCc = (addr: string, checked: boolean) => {
    setSelectedCc((prev) => {
      const next = new Set(prev);
      if (checked) next.add(addr);
      else next.delete(addr);
      return next;
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Send Royalty Statement</DialogTitle>
          <DialogDescription>
            Send statement for {displayPeriod} to {authorName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Email Address */}
          <div>
            <label className="text-sm font-medium">Email Address</label>
            {hasMultipleEmails && !useCustomEmail ? (
              <div className="mt-1 space-y-1">
                <select
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {authorEmails!.map((entry, idx) => (
                    <option key={`${entry.email}-${idx}`} value={entry.email}>
                      {entry.email} ({entry.source}{entry.isMain ? ", primary" : ""})
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="text-xs text-blue-600 hover:underline"
                  onClick={() => setUseCustomEmail(true)}
                >
                  Enter a different email
                </button>
              </div>
            ) : (
              <div className="mt-1 space-y-1">
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter email address"
                  aria-invalid={email.length > 0 && !emailValid}
                />
                {email.length > 0 && !emailValid && (
                  <p className="text-sm text-red-600">
                    Please enter a valid email address (e.g. name@example.com).
                  </p>
                )}
                {hasMultipleEmails && useCustomEmail && (
                  <button
                    type="button"
                    className="text-xs text-blue-600 hover:underline"
                    onClick={() => {
                      setUseCustomEmail(false);
                      setEmail(authorEmails![0].email);
                    }}
                  >
                    Choose from known emails
                  </button>
                )}
                {!authorEmail && !hasMultipleEmails && (
                  <p className="text-sm text-amber-600">
                    No email found. Please enter manually.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Subject */}
          <div>
            <label className="text-sm font-medium">Subject</label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject"
              className="mt-1"
            />
            {isCustomSubject && (
              <button
                type="button"
                className="text-xs text-blue-600 hover:underline mt-1"
                onClick={() => setSubject(DEFAULT_SUBJECT_SINGLE)}
              >
                Reset to default
              </button>
            )}
          </div>

          {/* Message */}
          <div>
            <button
              type="button"
              className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
              onClick={() => setShowMessage(!showMessage)}
            >
              {showMessage ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              Edit message
              {isCustomMessage && <Badge variant="secondary" className="ml-1 text-xs">edited</Badge>}
            </button>
            {showMessage && (
              <div className="mt-2 space-y-2">
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full min-h-[180px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-y"
                />
                {isCustomMessage && (
                  <button
                    type="button"
                    className="text-xs text-blue-600 hover:underline"
                    onClick={() => setMessage(defaultMessage)}
                  >
                    Reset to default
                  </button>
                )}
              </div>
            )}
          </div>

          {/* CC recipients (A5) */}
          {ccQuery.data && ccQuery.data.length > 0 && (
            <div>
              <label className="text-sm font-medium">CC <span className="font-normal text-xs text-muted-foreground">(pre-selected — untick any you don't want)</span></label>
              <div className="mt-1 space-y-1">
                {ccQuery.data.map((r) => (
                  <label key={r.id} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={selectedCc.has(r.emailAddress)}
                      onCheckedChange={(c) => toggleCc(r.emailAddress, c === true)}
                    />
                    <span>{r.displayName ? `${r.displayName} <${r.emailAddress}>` : r.emailAddress}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="bg-muted p-3 rounded-lg space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Attachments</span>
            </div>
            <div className="flex justify-between text-sm">
              <span>Royalty Statement PDF</span>
              <span className="font-medium">{formatCurrency(totalRoyalties, currency)}</span>
            </div>
            <div className="flex justify-between text-sm text-muted-foreground border-t pt-1 mt-1">
              <span>Wire Transfer Form</span>
              <span>.docx</span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSending}>
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={!emailValid || isSending || (showMessage && !message.trim()) || !subject.trim()}>
            {isSending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Mail className="h-4 w-4 mr-2" />
                Send Email
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// Account Summary Tab Component
// ============================================================================

// Payments tab now shows the FULL SAP ledger (all doc types) — view source
// dbo.vw_SAPRoyaltyPaymentHistory, no doc-type filter (Sanjay feedback 2026-05-29).
// Best-effort labels for SAP document types. Pending Tamara's authoritative glossary —
// shown as a tooltip; the code is the source of truth.
const DOC_TYPE_LABELS: Record<string, string> = {
  // Royalty statements (amounts owed to the author)
  KR: "Royalty statement",
  KG: "Royalty credit memo",
  // Payments (money paid out / cleared)
  SB: "Bank posting",
  KZ: "Vendor payment",
  ZP: "Payment posting",
  KA: "Vendor document",
  SA: "G/L account document",
  DA: "Customer document",
  AB: "Accounting document",
  // Clearing
  ZV: "Clearing entry",
};

const PaymentHistoryTab = ({ creditorId }: { creditorId: number }) => {
  const query = useQuery({
    queryKey: ["royalty-payment-history", creditorId],
    queryFn: () => getPaymentHistory(creditorId),
    enabled: !!creditorId,
    retry: 5,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
  const initialLoad = query.isFetching && !query.data;
  const isSlow = useSlowLoadingFlag(initialLoad);

  if (initialLoad) {
    return (
      <div className="space-y-4">
        {isSlow && <SlowLoadingNotice onReload={() => query.refetch()} />}
        <div className="flex items-center gap-2 text-muted-foreground text-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Loading payment history…</span>
        </div>
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
      </div>
    );
  }

  if (query.error) {
    return (
      <div className="text-center py-8 space-y-3">
        <div className="text-red-600">
          <AlertCircle className="h-8 w-8 mx-auto mb-2" />
          Failed to load payment history
        </div>
        <Button variant="outline" size="sm" onClick={() => query.refetch()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  const data = query.data;
  if (!data || data.rows.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-center text-muted-foreground">
            No SAP ledger entries found for this author.
          </p>
        </CardContent>
      </Card>
    );
  }

  const { rows, summary } = data;
  const docTotals = Object.entries(summary.totalsByCurrency);
  const localTotals = Object.entries(summary.localTotalsByCurrency ?? {});
  const fmtDate = (s: string | null) =>
    s ? new Date(s).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "—";
  const fmtAmt = (amt: number, ccy: string) =>
    `${ccy} ${amt.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Ledger entries</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.entryCount}</div>
            <p className="text-xs text-muted-foreground mt-1">
              All SAP rows · {summary.paymentCount} payment{summary.paymentCount === 1 ? "" : "s"}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">SAP balance (sum of all entries)</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Document currency is primary (authors are paid in document currency, not local CHF). */}
            {docTotals.length > 0 ? (
              <div className="space-y-1">
                {docTotals.map(([ccy, amt]) => (
                  <div key={ccy} className="text-2xl font-bold">{fmtAmt(amt, ccy)}</div>
                ))}
              </div>
            ) : localTotals.length > 0 ? (
              <div className="space-y-1">
                {localTotals.map(([ccy, amt]) => (
                  <div key={ccy} className="text-2xl font-bold">{fmtAmt(amt, ccy)}</div>
                ))}
              </div>
            ) : (
              <div className="text-2xl font-bold">—</div>
            )}
            {docTotals.length > 0 && localTotals.length > 0 && (
              <p className="text-xs text-muted-foreground mt-1">
                In local currency: {localTotals.map(([ccy, amt]) => fmtAmt(amt, ccy)).join(" · ")}
              </p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Last entry date</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{fmtDate(summary.lastPaymentDate)}</div>
            <p className="text-xs text-muted-foreground mt-1">First: {fmtDate(summary.firstPaymentDate)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Document date</TableHead>
                <TableHead>Document #</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Amount (doc)</TableHead>
                <TableHead className="text-right">Amount (local)</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Reference</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r: PaymentHistoryRow) => (
                <TableRow key={`${r.documentNumber}-${r.documentDate}`}>
                  <TableCell className="text-sm whitespace-nowrap">{fmtDate(r.documentDate)}</TableCell>
                  <TableCell className="font-mono text-xs">{r.documentNumber}</TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className="font-mono text-xs"
                      title={DOC_TYPE_LABELS[r.documentType] ?? "SAP document type"}
                    >
                      {r.documentType || "—"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">{r.description || "—"}</TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {fmtAmt(r.amountInDocCurr, r.documentCurrency)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm text-muted-foreground">
                    {fmtAmt(r.amountInLocalCurr, r.localCurrency)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={r.paymentStatus === "Cleared" ? "outline" : "secondary"}>
                      {r.paymentStatus || "—"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.reference || r.assignment || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={4} className="font-medium">Balance (sum of all entries)</TableCell>
                <TableCell className="text-right font-mono font-semibold">
                  {docTotals.map(([ccy, amt]) => fmtAmt(amt, ccy)).join(" · ") || "—"}
                </TableCell>
                <TableCell className="text-right font-mono font-semibold">
                  {localTotals.map(([ccy, amt]) => fmtAmt(amt, ccy)).join(" · ") || "—"}
                </TableCell>
                <TableCell colSpan={2} />
              </TableRow>
            </TableFooter>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

// Small info-icon tooltip used on the Account Summary metric cards so users
// understand exactly what each figure means (Sanjay feedback, 2026-05-29).
const CardInfo = ({ text }: { text: string }) => (
  <TooltipProvider delayDuration={150}>
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label="What does this mean?"
          className="text-muted-foreground/60 hover:text-muted-foreground focus:outline-none"
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-xs leading-relaxed">
        {text}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const AccountSummaryTab = ({
  creditorId,
  currency,
}: {
  creditorId: number;
  currency?: string;
}) => {
  const summaryQuery = useQuery({
    queryKey: ["royalty-account-summary", creditorId],
    queryFn: () => getAccountSummary(creditorId),
    enabled: !!creditorId,
  });
  const initialLoad = summaryQuery.isFetching && !summaryQuery.data;
  const isSlow = useSlowLoadingFlag(initialLoad);

  if (initialLoad) {
    return (
      <div className="space-y-4">
        {isSlow && <SlowLoadingNotice onReload={() => summaryQuery.refetch()} />}
        <div className="flex items-center gap-2 text-muted-foreground text-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Loading account summary…</span>
        </div>
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
      </div>
    );
  }

  if (summaryQuery.error) {
    return (
      <div className="text-center py-8 space-y-3">
        <div className="text-red-600">
          <AlertCircle className="h-8 w-8 mx-auto mb-2" />
          Failed to load account summary
        </div>
        <Button variant="outline" size="sm" onClick={() => summaryQuery.refetch()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  const summary = summaryQuery.data;
  if (!summary) return null;

  const curr = currency || summary.currency || "EUR";

  return (
    <div className="space-y-6">
      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
              Author's Share (Calculated)
              <CardInfo text="Lifetime author's share (payee share) calculated in Klopotek across all settlement periods (all years), in the statement currency — the figure shown on the signed PDF. Not gross revenue, and not what SAP shows as actually settled." />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(summary.totalRoyalties, summary.statementCurrency)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              All periods, from {summary.totalTitles} titles across {summary.totalLineItems} line items
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
              Author's Share (SAP-era)
              <CardInfo text="Author's share calculated from 2016 onward — the period the SAP ageing report covers — so it can be compared like-for-like against the SAP Account Balance. Excludes pre-2016 Klopotek history." />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-700">
              {formatCurrency(summary.sapComparableRoyaltyTotal, summary.statementCurrency)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Since 2016 — comparable to the SAP ageing report
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
              SAP Account Balance
              <CardInfo text="The author's current net balance in SAP's royalty ageing report. Negative = a credit owed to the author; positive = owed by the author. This is what SAP actually holds, independent of the Klopotek calculation." />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${summary.sapBalance < 0 ? 'text-blue-600' : 'text-gray-600'}`}>
              {formatCurrency(summary.sapBalance, curr)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {summary.sapBalance < 0 ? 'Credit (owed to author)' : 'Debit'}
            </p>
          </CardContent>
        </Card>

        <Card className="border-2 border-primary">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
              Outstanding Balance
              <CardInfo text="What Peter Lang still owes the author: the SAP Account Balance with its sign flipped (positive = owed to author). Zero or negative means the account is settled." />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${summary.outstandingBalance > 0 ? 'text-orange-600' : 'text-green-600'}`}>
              {formatCurrency(summary.outstandingBalance, curr)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {summary.outstandingBalance > 0 ? 'Owed to author' : 'Settled'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Royalty Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Copies Sold</span>
              <span className="font-medium">{formatNumber(summary.totalCopies)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Returns</span>
              <span className="font-medium">{formatNumber(summary.totalReturns)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Titles</span>
              <span className="font-medium">{summary.totalTitles}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">First Period</span>
              <span className="font-medium">
                {summary.firstPeriod ? formatPeriod(summary.firstPeriod) : '-'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Last Period</span>
              <span className="font-medium">
                {summary.lastPeriod ? formatPeriod(summary.lastPeriod) : '-'}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">SAP Transaction Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Transactions</span>
              <span className="font-medium">{summary.transactionCount}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Payment Documents</span>
              <span className="font-medium">{summary.paymentCount}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Payment Doc Total</span>
              <span className="font-medium">{formatCurrency(summary.totalPaymentDocuments, curr)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Royalty Doc Total</span>
              <span className="font-medium">{formatCurrency(summary.totalRoyaltyDocuments, curr)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Last Transaction</span>
              <span className="font-medium">
                {summary.lastTransactionDate
                  ? new Date(summary.lastTransactionDate).toLocaleDateString()
                  : '-'}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ============================================================================
// Quarterly Statements Tab Component
// ============================================================================

const QuarterlyStatementsTab = ({
  creditorId,
  authorName,
  currency,
}: {
  creditorId: number;
  authorName: string;
  currency?: string;
}) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [expandedQuarter, setExpandedQuarter] = useState<{ label: string; year: number; quarter: number } | null>(null);

  // Track which individual quarter is currently downloading/emailing
  const [downloadingQuarter, setDownloadingQuarter] = useState<string | null>(null);

  // Selection state for bulk operations
  const [selectedQuarters, setSelectedQuarters] = useState<Set<string>>(new Set());

  // Email resolution
  const emailQuery = useQuery({
    queryKey: ["royalty-author-email", creditorId],
    queryFn: () => getAuthorEmail(creditorId),
    enabled: !!creditorId,
    staleTime: 5 * 60_000,
  });

  const resolvedEmail = emailQuery.data?.email
    || emailQuery.data?.emails?.[0]?.email
    || null;
  const authorEmails = emailQuery.data?.emails;
  const bankAccountLast4 = emailQuery.data?.bankAccountLast4 || null;

  // Sent status tracking
  const sentStatusQuery = useQuery({
    queryKey: ["royalty-sent-status", creditorId],
    queryFn: () => getSentStatus(creditorId),
    enabled: !!creditorId,
    staleTime: 30_000,
  });

  const sentStatusMap = useMemo(() => {
    const map = new Map<string, { lastSentAt: string; lastSentBy: string; lastEmail: string; lastStatus: string; sendCount: number }>();
    if (sentStatusQuery.data) {
      for (const entry of sentStatusQuery.data) {
        // Normalize the date to YYYY-MM for matching against quarter end dates
        const d = new Date(entry.settlementPeriod);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        map.set(key, entry);
      }
    }
    return map;
  }, [sentStatusQuery.data]);

  // Preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewQuarter, setPreviewQuarter] = useState<QuarterlyStatement | null>(null);

  // Email state (single)
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailQuarter, setEmailQuarter] = useState<QuarterlyStatement | null>(null);

  // Bulk email dialog state
  const [bulkEmailOpen, setBulkEmailOpen] = useState(false);
  const [bulkEmailAddress, setBulkEmailAddress] = useState("");
  const [bulkSubject, setBulkSubject] = useState(DEFAULT_SUBJECT_BULK);
  const [bulkMessage, setBulkMessage] = useState("");
  const [bulkShowMessage, setBulkShowMessage] = useState(false);
  const [bulkUseCustomEmail, setBulkUseCustomEmail] = useState(false);
  const [bulkSelectedCc, setBulkSelectedCc] = useState<Set<string>>(new Set());
  const ccRecipientsQuery = useQuery({
    queryKey: ["royalty-cc-recipients", "active"],
    queryFn: () => getCcRecipients(true),
  });

  const quartersQuery = useQuery({
    queryKey: ["royalty-quarterly-statements", creditorId],
    queryFn: () => getQuarterlyStatements(creditorId),
    enabled: !!creditorId,
  });

  const linesQuery = useQuery({
    queryKey: ["royalty-quarterly-lines", creditorId, expandedQuarter?.label],
    queryFn: () => {
      if (!expandedQuarter) return [];
      return getQuarterlyLines(creditorId, expandedQuarter.year, expandedQuarter.quarter);
    },
    enabled: !!expandedQuarter,
  });

  // Preview query
  const previewQuery = useQuery({
    queryKey: ["royalty-quarterly-preview", creditorId, previewQuarter?.statementYear, previewQuarter?.quarterNum],
    queryFn: () => getQuarterlyStatementData(creditorId, previewQuarter!.statementYear, previewQuarter!.quarterNum),
    enabled: previewOpen && !!previewQuarter,
  });

  // Download mutation (single) — tracks which quarter via variables
  const downloadMutation = useMutation({
    mutationFn: (quarter: QuarterlyStatement) => {
      setDownloadingQuarter(quarter.quarterLabel);
      return downloadQuarterlyPdf(creditorId, quarter.statementYear, quarter.quarterNum, quarter.accountNumber);
    },
    onSuccess: () => {
      setDownloadingQuarter(null);
      toast({ title: "PDF Downloaded", description: "Quarterly statement PDF has been downloaded." });
    },
    onError: (error: Error) => {
      setDownloadingQuarter(null);
      toast({ title: "Download Failed", description: error.message, variant: "destructive" });
    },
  });

  // Send email mutation (single). On a duplicate-send block (409), confirm and retry with force.
  const sendEmailMutation = useMutation({
    mutationFn: async ({ quarter, email, customMessage, customSubject, cc }: { quarter: QuarterlyStatement; email: string; customMessage?: string; customSubject?: string; cc?: string[] }) => {
      try {
        return await sendQuarterlyEmail(creditorId, quarter.statementYear, quarter.quarterNum, email, undefined, customMessage, customSubject, cc);
      } catch (err) {
        if (err instanceof DuplicateSendError && window.confirm(`${err.message}\n\nClick OK to send it again anyway, or Cancel to stop.`)) {
          return sendQuarterlyEmail(creditorId, quarter.statementYear, quarter.quarterNum, email, undefined, customMessage, customSubject, cc, true);
        }
        throw err;
      }
    },
    onSuccess: (result) => {
      if (result.success) {
        toast({ title: "Statement queued", description: `Handed to the mail server for ${result.authorEmail}. Delivery is confirmed automatically within ~30 min (status shows Queued → Delivered, or Bounced if it fails).` });
        setEmailOpen(false);
        queryClient.invalidateQueries({ queryKey: ["royalty-sent-status", creditorId] });
      } else {
        toast({ title: "Email Failed", description: result.errorMessage || "Failed to send email", variant: "destructive" });
      }
    },
    onError: (error: Error) => {
      toast({ title: "Email Failed", description: error.message, variant: "destructive" });
    },
  });

  // Bulk download mutation
  const bulkDownloadMutation = useMutation({
    mutationFn: (quarters: { year: number; quarter: number }[]) =>
      downloadBulkQuarterlyPdf(creditorId, quarters, quartersQuery.data?.[0]?.accountNumber || creditorId),
    onSuccess: () => {
      toast({ title: "Zip Downloaded", description: `${selectedQuarters.size} statements downloaded.` });
      setSelectedQuarters(new Set());
    },
    onError: (error: Error) => {
      toast({ title: "Download Failed", description: error.message, variant: "destructive" });
    },
  });

  // Bulk email mutation. On a duplicate-send block (409), confirm and retry with force.
  const bulkEmailMutation = useMutation({
    mutationFn: async ({ quarters, email, customMessage, customSubject, cc }: { quarters: { year: number; quarter: number }[]; email: string; customMessage?: string; customSubject?: string; cc?: string[] }) => {
      try {
        return await sendBulkQuarterlyEmail(creditorId, quarters, email, undefined, customMessage, customSubject, cc);
      } catch (err) {
        if (err instanceof DuplicateSendError && window.confirm(`${err.message}\n\nClick OK to send them again anyway, or Cancel to stop.`)) {
          return sendBulkQuarterlyEmail(creditorId, quarters, email, undefined, customMessage, customSubject, cc, true);
        }
        throw err;
      }
    },
    onSuccess: (result) => {
      if (result.success) {
        toast({ title: "Statements queued", description: `${result.attachmentCount} statement(s) handed to the mail server for ${result.authorEmail}. Delivery is confirmed automatically within ~30 min (Queued → Delivered, or Bounced if it fails).` });
        setBulkEmailOpen(false);
        setSelectedQuarters(new Set());
        queryClient.invalidateQueries({ queryKey: ["royalty-sent-status", creditorId] });
      } else {
        toast({ title: "Email Failed", description: "Failed to send bulk email", variant: "destructive" });
      }
    },
    onError: (error: Error) => {
      toast({ title: "Email Failed", description: error.message, variant: "destructive" });
    },
  });

  // Selection helpers
  const toggleSelection = (label: string) => {
    setSelectedQuarters((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    const quarters = quartersQuery.data || [];
    if (selectedQuarters.size === quarters.length) {
      setSelectedQuarters(new Set());
    } else {
      setSelectedQuarters(new Set(quarters.map((q) => q.quarterLabel)));
    }
  };

  const getSelectedQuarterParams = (): { year: number; quarter: number }[] => {
    const quarters = quartersQuery.data || [];
    return quarters
      .filter((q) => selectedQuarters.has(q.quarterLabel))
      .map((q) => ({ year: q.statementYear, quarter: q.quarterNum }));
  };

  const handleBulkDownload = () => {
    bulkDownloadMutation.mutate(getSelectedQuarterParams());
  };

  const getBulkDefaultMessage = () => {
    return `Dear ${authorName},

I hope this message finds you well.

Please find attached your royalty statement(s) for your reference.

${bankAccountLast4
    ? `May I kindly request you to confirm your bank account details ending with ${bankAccountLast4}, so that we can proceed with the payment accordingly. If your bank details changed, we kindly request you to fill the attached wire transfer form and return it to us at your convenience.`
    : `We kindly request you to fill the attached wire transfer form and return it to us at your convenience.`}

This will help us process the payment smoothly and without any delay.

Should you have any questions regarding the statement or the payment process, please don't hesitate to contact us at royalties@peterlang.com.

Kind regards,
Your Peter Lang Royalty Team`;
  };

  const handleBulkEmailOpen = () => {
    setBulkEmailAddress(resolvedEmail || "");
    setBulkSubject(DEFAULT_SUBJECT_BULK);
    const defaultMsg = getBulkDefaultMessage();
    setBulkMessage(defaultMsg);
    setBulkShowMessage(false);
    setBulkUseCustomEmail(false);
    // Default-select all active CC recipients (Tamara's ask: don't add them manually each send).
    setBulkSelectedCc(new Set((ccRecipientsQuery.data ?? []).map((r) => r.emailAddress)));
    setBulkEmailOpen(true);
  };

  const handleBulkEmailSend = () => {
    if (!bulkEmailAddress) return;
    const defaultMsg = getBulkDefaultMessage();
    const isCustom = bulkMessage.trim() !== defaultMsg.trim();
    const isCustomSubject = bulkSubject.trim() !== DEFAULT_SUBJECT_BULK;
    bulkEmailMutation.mutate({
      quarters: getSelectedQuarterParams(),
      email: bulkEmailAddress,
      customMessage: isCustom ? bulkMessage : undefined,
      customSubject: isCustomSubject ? bulkSubject : undefined,
      cc: bulkSelectedCc.size > 0 ? Array.from(bulkSelectedCc) : undefined,
    });
  };

  if (quartersQuery.isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-16" />
        ))}
      </div>
    );
  }

  if (quartersQuery.error) {
    return (
      <div className="text-center py-8 text-red-600">
        <AlertCircle className="h-8 w-8 mx-auto mb-2" />
        Failed to load quarterly statements
      </div>
    );
  }

  const quarters = quartersQuery.data || [];

  if (quarters.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No quarterly statements found
      </div>
    );
  }

  const curr = currency || quarters[0]?.currency || "EUR";
  const hasSelection = selectedQuarters.size > 0;
  const allSelected = selectedQuarters.size === quarters.length;

  return (
    <>
      {/* Email resolution display */}
      <Card className="mb-4">
        <CardContent className="py-3 px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Email:</span>
              {emailQuery.isLoading ? (
                <Skeleton className="h-4 w-48" />
              ) : resolvedEmail ? (
                <span className="font-medium text-blue-600">{resolvedEmail}</span>
              ) : authorEmails && authorEmails.length > 0 ? (
                <span className="font-medium text-blue-600">{authorEmails[0].email}</span>
              ) : (
                <span className="text-amber-600">No email found</span>
              )}
              {bankAccountLast4 && (
                <span className="ml-3 text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                  Bank ****{bankAccountLast4}
                </span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions Bar */}
      {hasSelection && (
        <Card className="mb-4 border-blue-200 bg-blue-50">
          <CardContent className="py-3 px-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">
                {selectedQuarters.size} statement{selectedQuarters.size > 1 ? "s" : ""} selected
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleBulkDownload}
                  disabled={bulkDownloadMutation.isPending}
                >
                  {bulkDownloadMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  Download Zip
                </Button>
                <Button
                  size="sm"
                  onClick={handleBulkEmailOpen}
                  disabled={bulkEmailMutation.isPending}
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Email All
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Select All */}
      <div className="flex items-center gap-2 mb-2 px-1">
        <input
          type="checkbox"
          checked={allSelected}
          onChange={toggleSelectAll}
          className="h-4 w-4 rounded border-gray-300 cursor-pointer"
        />
        <span className="text-sm text-muted-foreground">
          {allSelected ? "Deselect all" : "Select all"}
        </span>
      </div>

      <div className="space-y-2">
        {quarters.map((quarter) => {
          const isExpanded = expandedQuarter?.label === quarter.quarterLabel;
          const isSelected = selectedQuarters.has(quarter.quarterLabel);
          // Check sent status for this quarter
          const qEnd = new Date(quarter.quarterEndDate);
          const sentKey = `${qEnd.getFullYear()}-${String(qEnd.getMonth() + 1).padStart(2, "0")}`;
          const sentInfo = sentStatusMap.get(sentKey);

          return (
            <Collapsible
              key={quarter.quarterLabel}
              open={isExpanded}
              onOpenChange={(open) =>
                setExpandedQuarter(open ? { label: quarter.quarterLabel, year: quarter.statementYear, quarter: quarter.quarterNum } : null)
              }
            >
              <Card className={isSelected ? "ring-2 ring-blue-300" : ""}>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {/* Checkbox */}
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            e.stopPropagation();
                            toggleSelection(quarter.quarterLabel);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="h-4 w-4 rounded border-gray-300 cursor-pointer"
                        />
                        {isExpanded ? (
                          <ChevronDown className="h-5 w-5" />
                        ) : (
                          <ChevronRight className="h-5 w-5" />
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <CardTitle className="text-base">
                              {quarter.quarterLabel}
                            </CardTitle>
                            {sentInfo && (() => {
                              // Delivery-aware badge. 'accepted' = handed to the mail server but
                              // NOT yet confirmed delivered (the reconciler upgrades it); 'bounced'
                              // = the mail server returned an undeliverable notice.
                              const status = sentInfo.lastStatus || "sent";
                              const when = new Date(sentInfo.lastSentAt).toLocaleString();
                              const times = sentInfo.sendCount > 1 ? ` (${sentInfo.sendCount} times)` : "";
                              const meta = `Last send to ${sentInfo.lastEmail} on ${when} by ${sentInfo.lastSentBy}${times}.`;
                              const cfg =
                                status === "delivered"
                                  ? { cls: "border-green-500 text-green-700 bg-green-50", Icon: CheckCircle2, label: "Delivered", tip: `Delivered. ${meta}` }
                                  : status === "bounced"
                                  ? { cls: "border-red-500 text-red-700 bg-red-50", Icon: AlertTriangle, label: "Bounced", tip: `Undeliverable — the recipient mail server returned a bounce. ${meta}` }
                                  : status === "accepted"
                                  ? { cls: "border-amber-500 text-amber-700 bg-amber-50", Icon: Clock, label: "Queued", tip: `Handed to the mail server; delivery not yet confirmed. ${meta}` }
                                  : { cls: "border-green-500 text-green-700 bg-green-50", Icon: CheckCircle2, label: "Sent", tip: `Sent (legacy record — delivery not tracked). ${meta}` };
                              const BadgeIcon = cfg.Icon;
                              return (
                                <Badge variant="outline" className={`${cfg.cls} text-xs`} title={cfg.tip}>
                                  <BadgeIcon className="h-3 w-3 mr-1" />
                                  {cfg.label}
                                </Badge>
                              );
                            })()}
                          </div>
                          <CardDescription>
                            Statement Date: {new Date(quarter.quarterEndDate).toLocaleDateString()}
                          </CardDescription>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {/* Action Buttons */}
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Preview Statement"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreviewQuarter(quarter);
                              setPreviewOpen(true);
                            }}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Download PDF"
                            disabled={downloadMutation.isPending}
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadMutation.mutate(quarter);
                            }}
                          >
                            {downloadingQuarter === quarter.quarterLabel ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Download className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Send Email"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEmailQuarter(quarter);
                              setEmailOpen(true);
                            }}
                          >
                            <Mail className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="text-right">
                          {/* Q1: author's share in the STATEMENT currency (not gross). Q3: when the
                              statement ccy differs from the payment ccy, show the payable below. */}
                          <div className="font-bold text-lg">
                            {formatCurrency(quarter.totalPayeeShare, quarter.statementCurrency)}
                          </div>
                          {quarter.statementCurrency !== quarter.currency && (
                            <div className="text-sm font-medium text-muted-foreground">
                              Payable: {formatCurrency(quarter.totalPayeeSharePayCcy, quarter.currency)}
                            </div>
                          )}
                          <div className="text-sm text-muted-foreground">
                            {quarter.lineItemCount} items • {formatNumber(quarter.totalCopies)} copies
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="pt-0">
                    {linesQuery.isLoading ? (
                      <div className="py-4">
                        <Skeleton className="h-32" />
                      </div>
                    ) : linesQuery.data && linesQuery.data.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>ISBN</TableHead>
                            <TableHead className="text-right">Copies</TableHead>
                            <TableHead className="text-right">Returns</TableHead>
                            <TableHead className="text-right">Rate</TableHead>
                            <TableHead className="text-right">Gross</TableHead>
                            <TableHead className="text-right">Payee %</TableHead>
                            <TableHead className="text-right">Author's Share</TableHead>
                            <TableHead>Territory</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {linesQuery.data.map((line, idx) => (
                            <TableRow key={`${line.isbn}-${idx}`}>
                              <TableCell className="font-mono text-sm">
                                {line.isbn || "-"}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatNumber(line.copies)}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatNumber(line.returns)}
                              </TableCell>
                              <TableCell className="text-right">
                                {line.slidingRate ? `${formatDecimal(line.slidingRate, 1)}%` : "-"}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatCurrency(line.royalty, line.statementCurrency)}
                              </TableCell>
                              <TableCell className="text-right">
                                {line.payeeSharePct !== null ? `${formatDecimal(line.payeeSharePct, 0)}%` : "-"}
                              </TableCell>
                              <TableCell className="text-right font-medium">
                                {formatCurrency(line.payeeShare, line.statementCurrency)}
                              </TableCell>
                              <TableCell>{line.territory || "-"}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <div className="py-4 text-center text-muted-foreground">
                        No line items found
                      </div>
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          );
        })}
      </div>

      {/* Preview Dialog */}
      <PreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        statementData={previewQuery.data || null}
        isLoading={previewQuery.isLoading}
      />

      {/* Single Email Dialog */}
      {emailQuarter && (
        <EmailDialog
          open={emailOpen}
          onOpenChange={setEmailOpen}
          authorName={authorName}
          period={emailQuarter.quarterEndDate}
          periodDisplay={emailQuarter.quarterLabel}
          authorEmail={resolvedEmail}
          authorEmails={authorEmails}
          totalRoyalties={emailQuarter.totalPayeeShare}
          currency={emailQuarter.statementCurrency}
          bankAccountLast4={bankAccountLast4}
          onSend={(email, customMessage, customSubject, cc) => sendEmailMutation.mutate({ quarter: emailQuarter, email, customMessage, customSubject, cc })}
          isSending={sendEmailMutation.isPending}
        />
      )}

      {/* Bulk Email Dialog */}
      <Dialog open={bulkEmailOpen} onOpenChange={setBulkEmailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Send {selectedQuarters.size} Statements</DialogTitle>
            <DialogDescription>
              All selected statements will be attached as PDFs to a single email.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Email Address */}
            <div>
              <label className="text-sm font-medium">Email Address</label>
              {authorEmails && authorEmails.length > 1 && !bulkUseCustomEmail ? (
                <div className="mt-1 space-y-1">
                  <select
                    value={bulkEmailAddress}
                    onChange={(e) => setBulkEmailAddress(e.target.value)}
                    className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {authorEmails.map((entry, idx) => (
                      <option key={`${entry.email}-${idx}`} value={entry.email}>
                        {entry.email} ({entry.source}{entry.isMain ? ", primary" : ""})
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="text-xs text-blue-600 hover:underline"
                    onClick={() => setBulkUseCustomEmail(true)}
                  >
                    Enter a different email
                  </button>
                </div>
              ) : (
                <div className="mt-1 space-y-1">
                  <Input
                    type="email"
                    value={bulkEmailAddress}
                    onChange={(e) => setBulkEmailAddress(e.target.value)}
                    placeholder="Enter email address"
                    aria-invalid={bulkEmailAddress.length > 0 && !isValidEmail(bulkEmailAddress)}
                  />
                  {bulkEmailAddress.length > 0 && !isValidEmail(bulkEmailAddress) && (
                    <p className="text-sm text-red-600">
                      Please enter a valid email address (e.g. name@example.com).
                    </p>
                  )}
                  {authorEmails && authorEmails.length > 1 && bulkUseCustomEmail && (
                    <button
                      type="button"
                      className="text-xs text-blue-600 hover:underline"
                      onClick={() => {
                        setBulkUseCustomEmail(false);
                        setBulkEmailAddress(authorEmails[0].email);
                      }}
                    >
                      Choose from known emails
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Subject */}
            <div>
              <label className="text-sm font-medium">Subject</label>
              <Input
                value={bulkSubject}
                onChange={(e) => setBulkSubject(e.target.value)}
                placeholder="Email subject"
                className="mt-1"
              />
              {bulkSubject.trim() !== DEFAULT_SUBJECT_BULK && (
                <button
                  type="button"
                  className="text-xs text-blue-600 hover:underline mt-1"
                  onClick={() => setBulkSubject(DEFAULT_SUBJECT_BULK)}
                >
                  Reset to default
                </button>
              )}
            </div>

            <div className="bg-muted p-3 rounded-lg space-y-1">
              <div className="text-sm font-medium mb-2">Statements to attach:</div>
              {quarters
                .filter((q) => selectedQuarters.has(q.quarterLabel))
                .map((q) => (
                  <div key={q.quarterLabel} className="flex justify-between text-sm">
                    <span>{q.quarterLabel}</span>
                    <span className="font-medium">{formatCurrency(q.totalPayeeShare, q.statementCurrency)}</span>
                  </div>
                ))}
              <div className="flex justify-between text-sm text-muted-foreground border-t pt-1 mt-1">
                <span>Wire Transfer Form</span>
                <span>.docx</span>
              </div>
            </div>

            <div>
              <button
                type="button"
                className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
                onClick={() => setBulkShowMessage(!bulkShowMessage)}
              >
                {bulkShowMessage ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                Edit message
                {bulkMessage.trim() !== getBulkDefaultMessage().trim() && (
                  <Badge variant="secondary" className="ml-1 text-xs">edited</Badge>
                )}
              </button>
              {bulkShowMessage && (
                <div className="mt-2 space-y-2">
                  <textarea
                    value={bulkMessage}
                    onChange={(e) => setBulkMessage(e.target.value)}
                    className="w-full min-h-[180px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-y"
                  />
                  {bulkMessage.trim() !== getBulkDefaultMessage().trim() && (
                    <button
                      type="button"
                      className="text-xs text-blue-600 hover:underline"
                      onClick={() => setBulkMessage(getBulkDefaultMessage())}
                    >
                      Reset to default
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* CC recipients (A5) */}
            {ccRecipientsQuery.data && ccRecipientsQuery.data.length > 0 && (
              <div>
                <label className="text-sm font-medium">CC <span className="font-normal text-xs text-muted-foreground">(pre-selected — untick any you don't want)</span></label>
                <div className="mt-1 space-y-1">
                  {ccRecipientsQuery.data.map((r) => (
                    <label key={r.id} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={bulkSelectedCc.has(r.emailAddress)}
                        onCheckedChange={(c) =>
                          setBulkSelectedCc((prev) => {
                            const next = new Set(prev);
                            if (c === true) next.add(r.emailAddress);
                            else next.delete(r.emailAddress);
                            return next;
                          })
                        }
                      />
                      <span>{r.displayName ? `${r.displayName} <${r.emailAddress}>` : r.emailAddress}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>Test send — set the recipient to:</span>
              <button
                className="text-blue-600 hover:underline"
                onClick={() => setBulkEmailAddress("royalties@peterlang.com")}
                title="Replaces the recipient above with royalties@peterlang.com. This does NOT send a copy — it changes who the statements go to (use it to send yourself a test)."
              >
                royalties@peterlang.com
              </button>
              <span className="italic">(replaces recipient — not a copy)</span>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkEmailOpen(false)} disabled={bulkEmailMutation.isPending}>
              Cancel
            </Button>
            <Button onClick={handleBulkEmailSend} disabled={!isValidEmail(bulkEmailAddress) || !bulkSubject.trim() || bulkEmailMutation.isPending || (bulkShowMessage && !bulkMessage.trim())}>
              {bulkEmailMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Send {selectedQuarters.size} Statements
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ============================================================================
// Main Page Component (Search)
// ============================================================================

export default function RoyaltyStatementsPage() {
  const [, setLocation] = useLocation();

  // Search state
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounced search
  const debouncedSetSearch = useCallback(
    debounce((value: string) => {
      setDebouncedSearch(value);
    }, 300),
    []
  );

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    debouncedSetSearch(value);
  };

  // Queries
  const searchQuery = useQuery({
    queryKey: ["royalty-author-search", debouncedSearch],
    queryFn: () => searchAuthors(debouncedSearch),
    enabled: debouncedSearch.length >= 2,
  });
  // Use isFetching (not isLoading) so spinner shows on every refetch / new query key.
  // React Query v5: isLoading is only true on the very first fetch with no data.
  const searchInFlight = searchQuery.isFetching;
  const searchIsSlow = useSlowLoadingFlag(searchInFlight);

  const handleSelectAuthor = (author: AuthorSearchResult) => {
    setLocation(`/royalty-statements/${author.creditorId}`);
  };

  const content = (
    <PageWrapper>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold">Royalty Statements</h1>
          <p className="text-muted-foreground">
            Search for an author by name, account number, or email
          </p>
        </div>

        {/* Search Box */}
        <Card>
          <CardContent className="pt-6">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, account number, or email..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10 text-lg h-12"
                autoFocus
              />
            </div>
            {searchTerm.length > 0 && searchTerm.length < 2 && (
              <p className="text-sm text-muted-foreground mt-2">
                Enter at least 2 characters to search
              </p>
            )}
          </CardContent>
        </Card>

        {/* Search Results */}
        {debouncedSearch.length >= 2 && (
          <div className="space-y-4">
            {searchInFlight ? (
              <div className="space-y-3">
                {searchIsSlow && <SlowLoadingNotice onReload={() => searchQuery.refetch()} />}
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Searching for "{debouncedSearch}"…</span>
                </div>
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-24" />
                ))}
              </div>
            ) : searchQuery.isError ? (
              <Card className="border-red-200 bg-red-50">
                <CardContent className="pt-6 flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-2 text-red-600">
                    <AlertCircle className="h-5 w-5" />
                    <p>Search failed: {(searchQuery.error as Error).message}</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => searchQuery.refetch()}>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Retry
                  </Button>
                </CardContent>
              </Card>
            ) : searchQuery.data?.authors.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No authors found matching "{debouncedSearch}"</p>
                </CardContent>
              </Card>
            ) : (() => {
              const authorGroups = groupAuthorSearchResults(searchQuery.data?.authors ?? []);
              const accountCount = searchQuery.data?.total ?? 0;
              return (
                <>
                  <div className="text-sm text-muted-foreground">
                    Found {authorGroups.length} author{authorGroups.length !== 1 ? "s" : ""}
                    {accountCount !== authorGroups.length ? ` · ${accountCount} accounts` : ""}
                  </div>
                  <div className="space-y-3">
                    {authorGroups.map((group) =>
                      group.authors.length === 1 ? (
                        <AuthorCard
                          key={group.key}
                          author={group.authors[0]}
                          onClick={() => handleSelectAuthor(group.authors[0])}
                        />
                      ) : (
                        <GroupedAuthorCard
                          key={group.key}
                          group={group}
                          onSelect={handleSelectAuthor}
                        />
                      )
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* Empty state when no search */}
        {debouncedSearch.length < 2 && (
          <Card>
            <CardContent className="py-12 text-center">
              <User className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-medium mb-2">Search for an Author</h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                Enter an author's name, account number, or email address to find their royalty statements.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </PageWrapper>
  );

  return <ViewGuard section="royaltyStatements">{content}</ViewGuard>;
}

// ============================================================================
// Account Detail Page (/royalty-statements/:creditorId)
// ============================================================================

export function RoyaltyStatementAccountPage({ creditorId }: { creditorId: number }) {
  const [, setLocation] = useLocation();

  const summaryQuery = useQuery({
    queryKey: ["royalty-account-summary", creditorId],
    queryFn: () => getAccountSummary(creditorId),
    enabled: !!creditorId,
  });
  // Show spinner on initial load (no data yet). For background refetches we keep showing existing data.
  const summaryInitialLoad = summaryQuery.isFetching && !summaryQuery.data;
  const summaryIsSlow = useSlowLoadingFlag(summaryInitialLoad);

  const summary = summaryQuery.data;

  if (summaryInitialLoad) {
    return (
      <ViewGuard section="royaltyStatements">
        <PageWrapper>
          <div className="space-y-4">
            {summaryIsSlow && <SlowLoadingNotice onReload={() => summaryQuery.refetch()} />}
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Loading account summary…</span>
            </div>
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-[400px]" />
          </div>
        </PageWrapper>
      </ViewGuard>
    );
  }

  if (summaryQuery.isError || !summary) {
    return (
      <ViewGuard section="royaltyStatements">
        <PageWrapper>
          <div className="space-y-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLocation("/royalty-statements")}
              className="mb-2"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to search
            </Button>
            <Card className="border-red-200 bg-red-50">
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 text-red-600">
                  <AlertCircle className="h-5 w-5" />
                  <p>Account not found or failed to load.</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </PageWrapper>
      </ViewGuard>
    );
  }

  return (
    <ViewGuard section="royaltyStatements">
      <PageWrapper>
        <div className="space-y-6">
          {/* Back Button + Header */}
          <div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLocation("/royalty-statements")}
              className="mb-2"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to search
            </Button>
            <h1 className="text-2xl font-bold">{summary.authorName}</h1>
            <div className="flex items-center gap-4 text-muted-foreground mt-1">
              <span>Account: {summary.accountNumber}</span>
            </div>
          </div>

          {/* Tabbed Content */}
          <Tabs defaultValue="summary" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-4">
              <TabsTrigger value="summary" className="flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Account Summary
              </TabsTrigger>
              <TabsTrigger value="quarterly" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Quarterly Statements
              </TabsTrigger>
              <TabsTrigger value="payments" className="flex items-center gap-2">
                <Receipt className="h-4 w-4" />
                Payments
              </TabsTrigger>
            </TabsList>

            <TabsContent value="summary">
              <AccountSummaryTab
                creditorId={creditorId}
                currency={summary.currency}
              />
            </TabsContent>

            <TabsContent value="quarterly">
              <QuarterlyStatementsTab
                creditorId={creditorId}
                authorName={summary.authorName}
                currency={summary.currency}
              />
            </TabsContent>

            <TabsContent value="payments">
              <PaymentHistoryTab creditorId={creditorId} />
            </TabsContent>
          </Tabs>
        </div>
      </PageWrapper>
    </ViewGuard>
  );
}
